import { Component, OnInit, OnDestroy } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { FormControl, FormGroup, FormBuilder } from '@angular/forms';
// services
import { MasterService } from "../../services/master.service";
import { MessageService } from "../../services/message.service";
import { StoreService } from "../../services/store.service";
import { SecurityService } from "src/app/services/security.service";
import * as languageLibrary from '../../services/language';
import { SEARCH_DEBOUNCE_MILLIS } from "src/app/constants/list";
import { Favorite } from "src/app/models/favorite.model";
import { Report } from "src/app/models/report.model";
import { Subscription } from 'rxjs';

@Component({
    selector: "reports",
    templateUrl: "./reports.component.html",
    styleUrls: ["./reports.component.scss"],
    standalone: false
})
export class ReportsComponent implements OnInit, OnDestroy {

    /** define if is loading */ 
    protected loading: boolean = false

    // save the language
    protected language: string = localStorage.getItem('language') ? localStorage.getItem('language') : 'EN'

    // set all words
    protected words = languageLibrary.language

    // The search form group
    protected search: FormGroup | null = null

    /** A list of report groups and associated reports. */
    protected groups: Object = {}

    private favs: Favorite[] = []

    // A debounce timer for search events. Used to wait for 
    // the user to stop typing before we fetch.
    private watcher: ReturnType<typeof setTimeout> = null

    private subscriptions: Subscription[] = []

    /** Get a list of report groups */
    get groupKeys(): string[] {
        return Object.keys(this.groups)
    }

    constructor(private master: MasterService, private ms: MessageService, protected store: StoreService, private router: Router, private fb: FormBuilder, protected security: SecurityService) {
        this.search = this.fb.group({
            inquiry: new FormControl<string>('', [])
        })
    }

ngOnInit() {
        this.fetchFavs()
        this.fetch()

        this.subscriptions.push(
            this.search.get('inquiry').valueChanges.subscribe(inquiry => {
                if (this.watcher) {
                    clearTimeout(this.watcher)
                    this.watcher = null
                }
        
                this.watcher = setTimeout(() => {
                    this.fetch()
                }, SEARCH_DEBOUNCE_MILLIS)
            })
        )
    }

    ngOnDestroy() {
        for (const sub of this.subscriptions) {
            sub.unsubscribe()
        }
        if (this.watcher) {
            clearTimeout(this.watcher)
        }
    }

    /**
     * Get a list of reports the user has access to.
     */
    private fetch = async () => {
        if (this.loading) {
            return
        }

        let inquiryParam = ''
        const inquiry = this.search.get('inquiry').value
        if (inquiry && (inquiry.trim().length > 0)) {
            inquiryParam = `?q=${encodeURIComponent(inquiry)}`
        }

        this.loading = true
        this.groups = {}

        await this.master.getAsync(`reports${inquiryParam}`).then(res => {
            this.loading = false

            if (!res || !res.data) {
                return
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error ? res.data.error : this.words[this.language]['apiNoResponse'] });
                return
            }

            if (!res.data.reports || (res.data.reports.length <= 0)) {
                return
            }

            this.constructGroups(res.data.reports)
        })
    }

    /**
     * Contrcut the report groups based on the list of reports received from the API
     * @param reports 
     */
    private constructGroups = (reports: [Report]) => {
        if (!reports || (reports.length <= 0)) {
            return
        }

        for (const report of reports) {
            const group = report.group

            if (!this.groups[group]) {
                this.groups[group] = []
            }

            this.groups[group].push({
                id: report.id,
                token: report.token,
                name: report.name,
                description: report.description
            })
        }
    }

    /**
     * Get a list of the current users favorite reports.
     */
    private fetchFavs = async () => {
        await this.master.getAsync(`favorites?resource=reports`).then(res => {
            if (!res || !res.data) {
                return
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error ? res.data.error : this.words[this.language]['apiNoResponse'] });
                return
            }

            if (!res.data.favs) {
                this.favs = []
                return
            }
            
            this.favs = res.data.favs
        })
    }

    /**
     * Toggle the user report favorite on and off based on the current state.
     * @param label The human-readable friendly name of the report.
     * @param identity Unique path to the report (/reports/{identity})
     */
    protected toggleFavorite = async (label: string, identity: string) => {
        if (!this.favs || (this.favs.length <= 0)) {
            await this.addFav(label, identity)
            return
        }

        const found = this.favs.find((fav) => fav.i === identity)
        if (!found) {
            await this.addFav(label, identity)
            return
        }

        await this.removeFav(identity)
    }

    /**
     * Add the user report favorite.
     * @param label The human-readable friendly name of the report.
     * @param identity Unique path to the report (/reports/{identity}) 
     */
    private addFav = async (label: string, identity: string) => {
        const meta = {
            label: label,
            resource: 'reports',
            i: identity
        }

        await this.master.post(`favorites`, meta, (res) => {
            this.loading = false

            if (!res || !res.data) {
                return
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error ? res.data.error : this.words[this.language]['apiNoResponse'] });
                return
            }

            this.favs.push(res.data.favorite)
        })
    }

    /**
     * Remove the user report favorite.
     * @param identity Unique path to the report (/reports/{identity})
     */
    private removeFav = async (identity: string) => {
        await this.master.discard(`favorites?resource=reports&i=${encodeURIComponent(identity)}`, (res) => {
            this.loading = false

            if (!res || !res.data) {
                return
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error ? res.data.error : this.words[this.language]['apiNoResponse'] });
                return
            }

            const index = this.favs.findIndex((fav) => fav.i === identity)
            if (index < 0) {
                return
            }

            this.favs.splice(index, 1)
        })
    }

    /**
     * Return the icon to use based on the given report identity.
     * @param identity Unique path to the report (/reports/{identity})
     */
    protected icon = (identity) => {
        if (!this.favs || (this.favs.length <= 0)) {
            return 'favorite_border'
        }

        const found = this.favs.find((fav) => fav.i === identity)
        if (!found) {
            return 'favorite_border'
        }

        return 'favorite'
    }
    
    /**
     * listen to output of navbar
    */ 
    public listenerNav = (e): void => {
        switch (e['message']) {
            case 'changeLanguage':
                this.language = e['value']
                break
            case 'changeLot':
                this.fetch()
                break
        }
    }
}
